import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { toast } from 'sonner';
import '@/App.css';
import { Toaster } from './components/ui/sonner';

// When any admin request comes back 401 (e.g. an expired login token),
// clear the token and send the user back to the login screen with a clear
// message — instead of silently failing (e.g. image uploads).
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    const status = error.response?.status;
    if (status === 401 && !url.includes('/auth/login')) {
      localStorage.removeItem('admin_token');
      const path = window.location.pathname;
      if (path.startsWith('/admin') && path !== '/admin/login') {
        toast.error('Your session expired. Please log in again.');
        setTimeout(() => { window.location.href = '/admin/login'; }, 800);
      }
    }
    return Promise.reject(error);
  }
);

// Lazy load all page components for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const ColourMachinesPage = lazy(() => import('./pages/ColourMachinesPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const RentalPage = lazy(() => import('./pages/RentalPage'));
const LaserMachinePage = lazy(() => import('./pages/LaserMachinePage'));
const SharpBP20M31Page = lazy(() => import('./pages/SharpBP20M31Page'));
const AcrylicFramePage = lazy(() => import('./pages/AcrylicFramePage'));
const KyoceraTaskalfaMZPage = lazy(() => import('./pages/KyoceraTaskalfaMZPage'));
const BlogsPage = lazy(() => import('./pages/BlogsPage'));
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));

// Admin pages - loaded only when needed
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminBlogs = lazy(() => import('./pages/admin/AdminBlogs'));
const AdminContacts = lazy(() => import('./pages/admin/AdminContacts'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminGallery = lazy(() => import('./pages/admin/AdminGallery'));

// Fire a Meta Pixel PageView + self-hosted analytics hit on every route change
// (the pixel is initialised in index.html without an initial PageView, so this
// covers the first load too). Admin pages are not tracked.
const PageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
    if (!location.pathname.startsWith('/admin')) {
      axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/track`, {
        path: location.pathname,
        referrer: document.referrer || null
      }).catch(() => {});
    }
  }, [location.pathname]);
  return null;
};

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <PageTracker />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/:slug" element={<ProductDetailPage />} />
            <Route path="/colour-machines" element={<ColourMachinesPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/photocopier-printer-on-rent" element={<RentalPage />} />
            <Route path="/co2-laser-machine" element={<LaserMachinePage />} />
            <Route path="/sharp-bp20m31" element={<SharpBP20M31Page />} />
            <Route path="/diy-acrylic-photo-frame" element={<AcrylicFramePage />} />
            <Route path="/kyocera-taskalfa-mz9500ci-mz10500i" element={<KyoceraTaskalfaMZPage />} />
            <Route path="/blogs" element={<BlogsPage />} />
            <Route path="/blogs/:id" element={<BlogDetailPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/products" element={<AdminProducts />} />
            <Route path="/admin/blogs" element={<AdminBlogs />} />
            <Route path="/admin/contacts" element={<AdminContacts />} />
            <Route path="/admin/gallery" element={<AdminGallery />} />
            <Route path="/admin/settings" element={<AdminSettings />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </HelmetProvider>
  );
}

export default App;